import { Skeleton } from 'components/ui/skeleton';
import React from 'react';
import NumberFormat from 'react-number-format';
import { LawFirmStatisticsDto } from 'types/law-firms';
import { t } from 'utils/getTranslations';

interface Props {
    statistics: LawFirmStatisticsDto | undefined;
    isLoading: boolean;
}

const formatPercentageWithSign = (value: number | null): string => {
    if (value === null) return '-';
    const percentage = value * 100;
    const sign = percentage >= 0 ? '+' : '';
    return `${sign}${percentage.toFixed(0)}%`;
};

const CARD_CLASS =
    'border-divider bg-paper min-w-[200px] flex-[0_0_calc(25%_-_12px)] new:flex-[0_0_calc(25%-var(--card-gap)*0.75)] rounded-[10px] border p-5 new:border-0 new:shadow-card new:outline-hidden';
const TITLE_CLASS = 'text-body1 text-fg-secondary mb-2 font-medium';
const VALUE_CLASS = 'text-[2rem] leading-[1.2] font-semibold';
const SUBTITLE_CLASS = 'text-body1 text-fg-secondary';

const LawFirmsStatisticsHeader: React.FC<Props> = ({ statistics, isLoading }) => {
    if (isLoading) {
        return (
            <div className="new:gap-(--card-gap) flex gap-4">
                {[1, 2].map((i) => (
                    // oxlint-disable-next-line react-doctor/no-array-index-as-key -- iterating fixed [1,2] values; static skeleton loader cards
                    <div key={i} className={CARD_CLASS}>
                        <Skeleton className="h-5 w-3/5" />
                        <Skeleton className="mt-2 h-12 w-4/5" />
                        <Skeleton className="mt-2 h-5 w-2/5" />
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="new:gap-(--card-gap) flex flex-wrap gap-4">
            {/* Sector Contacts */}
            <section className={CARD_CLASS}>
                <p className={TITLE_CLASS}>{t('law-firms.stats.sector-contacts')}</p>
                <div className="flex items-baseline gap-2">
                    <p className={VALUE_CLASS}>{statistics?.sectorContactCount ?? '-'}</p>
                    <p className={SUBTITLE_CLASS}>
                        {statistics?.sectorContactsCreatedThisQuarter !== null && (
                            <span className="text-success">+{statistics?.sectorContactsCreatedThisQuarter ?? 0}</span>
                        )}{' '}
                        {t('law-firms.stats.this-quarter')}
                    </p>
                </div>
            </section>

            {/* Total Sector Revenue (3Y) */}
            <section className={CARD_CLASS}>
                <p className={TITLE_CLASS}>{t('law-firms.stats.total-sector-revenue')}</p>
                <div className="flex items-baseline gap-2">
                    <p className={VALUE_CLASS}>
                        {statistics?.totalSectorRevenue3y !== null ? (
                            <NumberFormat
                                displayType="text"
                                value={statistics?.totalSectorRevenue3y ?? 0}
                                thousandSeparator={' '}
                                decimalScale={0}
                                fixedDecimalScale={false}
                            />
                        ) : (
                            '-'
                        )}
                    </p>
                    <p className={SUBTITLE_CLASS}>
                        {t('law-firms.stats.vs-ly')}:{' '}
                        <span className={(statistics?.sectorRevenueChangeRate ?? -1) >= 0 ? 'text-success' : undefined}>
                            {formatPercentageWithSign(statistics?.sectorRevenueChangeRate ?? null)}
                        </span>
                    </p>
                </div>
            </section>
        </div>
    );
};

export default LawFirmsStatisticsHeader;
